// frontend-react/src/components/player/ProjectionInsights.js
// Chunk 31 — user-facing v3 product surfaces on the player card:
//  • IL Risk gauge (30/60/season) + documented-tolerance note + top drivers
//  • Anomaly flag (gap, direction, percentile) + SHAP-traced narrative bullets
//  • Profile Comparables (overhaul Gate 2a/2c; 2026-07 track-record rebuild):
//    physical-profile kNN served as P9's track-record proof — four OPS columns
//    + CONVERGED/DIVERGED verdicts + convergence aggregate; survivorship
//    honesty as a footnote; labeled absent states for pitcher /
//    insufficient_sample / not_current (Gate 3)
// All status-safe: a surface renders only when its data is present.
//
// UI grammar: each surface is a COLLAPSIBLE card (collapsed by default) so the
// Overview tab is not jammed open. Card chrome + collapse pattern match the rest
// of the DD UI — the dark panel background (rgba(23,23,23,0.5) on a
// rgba(55,65,81,0.5) border, same as PlayerOverviewTab's panels) and the
// clickable header + ChevronDown/ChevronRight swap (gold icon) from
// PlayerAdvancedAnalytics. No indigo/blue fills; status colors use DD tokens.
import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { useProjectionInsights } from '../../hooks/useProjectionInsights';
import SparkGlyph from '../common/SparkGlyph';

const C = dynastyTheme.tokens.colors;
const pct = (v) => (v == null ? '—' : `${Math.round(v * 100)}%`);

// DD dark card chrome — replaces the prior indigo #1a1a2e / #2a2a3e fill.
const cardChrome = {
  background: 'rgba(23,23,23,0.5)',
  border: '1px solid rgba(55,65,81,0.5)',
  borderRadius: 8,
  marginTop: 10,
  overflow: 'hidden',
};
const headerRow = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '8px 12px', cursor: 'pointer',
};
const titleStyle = {
  color: C.primary, fontWeight: 700, fontSize: 12,
  letterSpacing: 0.6, textTransform: 'uppercase',
};
const bodyStyle = { padding: '0 12px 12px' };
const sub = { color: C.neutral[400], fontSize: 12 };

// Reuses the DD collapsible grammar (clickable header + Chevron swap, gold icon)
// from PlayerAdvancedAnalytics. Collapsed by default.
const CollapsibleCard = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={cardChrome}>
      <div style={headerRow} onClick={() => setOpen((v) => !v)}>
        <span style={titleStyle}>{title}</span>
        {open
          ? <ChevronDown size={14} style={{ color: C.primary }} />
          : <ChevronRight size={14} style={{ color: C.primary }} />}
      </div>
      {open && <div style={bodyStyle}>{children}</div>}
    </div>
  );
};

const ILRiskBody = ({ il }) => {
  // Only ever reached for a REAL per-player payload — the parent hides the whole
  // section for the gated/population-baseline shape (Tony ruling 2026-07-15), so
  // there is no population branch here anymore.
  const H = il.horizons || {};
  const band = (p) => (p >= 0.5 ? C.error : p >= 0.25 ? C.warning : C.success);
  const Row = ({ label, d }) => d ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '3px 0' }}>
      <span style={{ width: 70, ...sub }}>{label}</span>
      <div style={{ flex: 1, height: 8, background: 'rgba(55,65,81,0.3)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: pct(d.probability), height: '100%', background: band(d.probability) }} />
      </div>
      <span style={{ width: 42, textAlign: 'right', color: C.neutral[200], fontSize: 12 }}>{pct(d.probability)}</span>
    </div>
  ) : null;
  return (
    <>
      <Row label="Next 30d" d={H['30d']} />
      <Row label="Next 60d" d={H['60d']} />
      <Row label="Rest of season" d={H['season']} />
      {il.top_drivers?.length > 0 && (
        <div style={{ ...sub, marginTop: 6 }}>
          Drivers: {il.top_drivers.map((x) => `${x.feature.replace(/_/g, ' ')} (${x.direction === 'increases_risk' ? '↑' : '↓'})`).join(', ')}
        </div>
      )}
      <div style={{ ...sub, marginTop: 6, fontStyle: 'italic' }}>
        Calibrated probability, not a sharp call — injury timing is inherently noisy.
      </div>
    </>
  );
};

const ProjectionTrendBody = ({ series, target }) => {
  const vals = series.values;
  const latest = vals[vals.length - 1];
  const single = series.count <= 1;
  // DD-RO-BUGS6-0725 B2: the per-point calculation_date sits in the payload (series.series[].calculation_date)
  // but the chart only plotted the flattened values, so the line had NO time axis and no explanation — a
  // reader could read it as the player's game-to-game results. Surface first/last date ticks under the
  // glyph and say plainly what the line is: OUR forecast as the model retrains, not his actual games.
  const rows = Array.isArray(series.series) ? series.series : [];
  const dates = rows.map((r) => fmtDate(r.calculation_date)).filter(Boolean);
  const first = dates[0];
  const last = dates[dates.length - 1];
  const tgt = target ? `${target} ` : '';
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <SparkGlyph values={vals} w={120} h={28} />
          {!single && first && last && (
            <div style={{ display: 'flex', justifyContent: 'space-between', width: 120, fontSize: 9, color: C.neutral[500] }}>
              <span>{first}</span><span>{last}</span>
            </div>
          )}
        </div>
        <span style={{ color: C.neutral[200], fontSize: 13 }}>{latest != null ? latest.toFixed(2) : '—'}</span>
      </div>
      <div style={{ ...sub, marginTop: 6 }}>
        {single
          ? `Projection history begins — the trend builds as we reproject his ${tgt}each day.`
          : `How our ${tgt}projection for him has moved over the last ${series.window_days} days as the model retrains — this tracks our forecast, not his game results.`}
      </div>
    </>
  );
};

// B3 (2026-07-14): the anomaly reads in HUMAN UNITS — which stat, actual vs
// projected in that stat's units (served fields only, no invented numbers; the
// raw gap value never renders as copy). Peer framing comes from peer_context —
// the SAME direction-conditioned thin-state-honest comps engine as Profile
// Comparables (one engine, one honesty standard) — never the legacy
// 'similar-pattern peers' percentile.
const RATE3 = new Set(['AVG', 'OBP', 'SLG', 'OPS', 'WOBA']);
const RATE2 = new Set(['ERA', 'WHIP', 'BB9', 'HR9', 'K9', 'FIP']);
const fmtStat = (v, t) => {
  if (v == null) return '—';
  const n = Number(v);
  if (RATE3.has(t)) return n.toFixed(3);
  if (RATE2.has(t)) return n.toFixed(2);
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

// DEFECT-4: window label from the served window descriptor (season-to-date
// cumulative through the archive's write date — invent nothing; if as_of is
// absent, the basis alone still frames the window).
const fmtDate = (s) => {
  if (!s) return null;
  const m = String(s).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${MON[Number(m[2]) - 1]} ${Number(m[3])}`;
};
const windowLabel = (w) => {
  if (!w) return null;
  if (w.basis === 'season_to_date') {
    const d = fmtDate(w.as_of);
    return d ? `season to date, through ${d}` : 'season to date';
  }
  return null;
};

const AnomalyBody = ({ anomaly }) => {
  const over = anomaly.direction === 'overperforming';
  const t = anomaly.target_stat;
  const hasUnits = anomaly.actual != null && anomaly.projected_value != null;
  const delta = hasUnits ? anomaly.actual - anomaly.projected_value : null;
  const pc = anomaly.peer_context;
  const win = windowLabel(anomaly.window);
  // ONE-VOICE (Phase 2): this is a COUNTING-stat PROJECTION anomaly — a DIFFERENT object
  // from the OPS skill verdict. Render the backend's per-stat label VERBATIM ("HR: pace
  // +1.7 vs projection"), never a bare over/under call that reads like a skill judgment.
  // contradicts_ops_verdict => keep it explicitly separated (a pace note, not the verdict).
  return (
    <>
      <div style={{ color: C.neutral[200], fontWeight: 600, marginBottom: 4 }}>
        <span style={{ color: over ? C.success : C.warning, fontWeight: 500 }}>
          {anomaly.per_stat_label || anomaly.direction?.replace(/_/g, ' ')}
        </span>
      </div>
      {anomaly.per_stat_label && (
        <div style={{ ...sub, marginTop: 2, fontStyle: 'italic' }}>
          Counting-stat pace — read separately from the skill-vs-surface signal
          {anomaly.contradicts_ops_verdict ? ' (points a different way here).' : '.'}
        </div>
      )}
      {hasUnits && (
        <div style={sub}>
          {win ? `${win} — ` : ''}{t}: {fmtStat(anomaly.actual, t)} actual vs {fmtStat(anomaly.projected_value, t)} projected
          {' '}({delta >= 0 ? '+' : '−'}{fmtStat(Math.abs(delta), t)} {t} vs expectation)
        </div>
      )}
      {pc?.available && pc.thin && (
        <div style={{ ...sub, marginTop: 4, fontStyle: 'italic' }}>
          Not enough same-situation history for peer framing — pool too thin to be honest.
        </div>
      )}
      {pc?.available && !pc.thin && pc.base_rate_line && (
        <div style={{ ...sub, marginTop: 4 }}>{pc.base_rate_line}</div>
      )}
      {anomaly.shap_narrative?.some((b) => b.label) && (
        <ul style={{ ...sub, marginTop: 6, paddingLeft: 16 }}>
          {/* Backend (comparable_finder _drivers) whitelisted + labeled these physical
              drivers; render the label verbatim, never a raw feature name. */}
          {anomaly.shap_narrative.filter((b) => b.label).slice(0, 3).map((b, i) => (
            <li key={i}>{b.label} {b.direction === 'up' ? '↑' : '↓'}</li>
          ))}
        </ul>
      )}
    </>
  );
};

// Profile Comparables — the comps table is P9's TRACK-RECORD PROOF (2026-07
// rebuild): the backend serves four OPS columns per comp (ops_then / p9_expected
// / what_happened via the single wOBA→OPS map, fit 863045bd, replicated
// server-side from skill_to_ops) + the canonical 5-category verdict (STRONG/HIT/
// FLAT/MISS/PENDING, same scorer as the P9 Track Record), plus the same-situation
// aggregate for the header line. No raw wOBA is displayed anywhere;
// the survivorship honesty (converged/flat/vanished) is a FOOTNOTE line, not
// the display concept. Gate 3 absent states render for covered:false reasons;
// the gap/SHAP comps no longer render here (Gate 2b: endpoint stays for a
// future labeled power view).
const ops3 = (v) => (v == null ? '—' : Number(v).toFixed(3));
// Only 'pitcher' and 'insufficient_sample' are user-facing absent states. The
// serve path self-lifts any well-sampled hitter on a miss, so nightly-board
// membership ('not_current' — an internal writer detail) is never surfaced.
const PROFILE_COMPS_EMPTY = {
  pitcher: 'Profile comps cover hitters with stabilized contact profiles.',
  insufficient_sample: 'Not enough stabilized sample yet for reliable comps (needs ~50 batted balls).',
};
// CANONICAL 5-category direction-relative verdict (STRONG/HIT/FLAT/MISS/PENDING),
// identical to the P9 Track Record "Model Result". The call moving the predicted
// way (STRONG/HIT) is a win; opposite move (MISS) is warning; no material move
// (FLAT) or no forward sample yet (PENDING) is neutral.
const VERDICT_COLOR = {
  STRONG: C.success, HIT: C.success, FLAT: C.neutral[400], MISS: C.warning, PENDING: C.neutral[400],
};

const ProfileCompsBody = ({ profileComps }) => {
  if (!profileComps.covered) {
    return (
      <div style={sub}>
        {PROFILE_COMPS_EMPTY[profileComps.reason] || 'Profile comps unavailable for this player.'}
      </div>
    );
  }
  // PHASE 2 GATE: divergence signal too low to show comps — SUPPRESS the table/rate and
  // render an EXPLICIT honest statement in its place (never blank, never a 50% table).
  // The gate is decided server-side on the validated divergence axis.
  if (!profileComps.show) {
    return (
      <div style={{ ...sub, fontStyle: 'italic' }}>
        {profileComps.collapse_line
          || "No high-confidence call for this player's underlying signal — his skill-vs-surface divergence is within the coin-flip range, so we're not showing comparable outcomes we can't stand behind."}
      </div>
    );
  }
  const p = profileComps.profile || {};
  const comps = profileComps.comps || [];
  return (
    <>
      <div style={sub}>
        Physical-profile similarity (stabilized contact profile: EV, barrel%, hard-hit%, launch, xwOBA)
      </div>
      {p.n_bbe != null && (
        <div style={{ ...sub, marginTop: 4 }}>
          This profile (last {p.n_bbe} batted balls): EV {p.ev != null ? `${p.ev} mph` : '—'} ·
          {' '}Barrel {p.barrel_pct != null ? `${p.barrel_pct}%` : '—'} ·
          {' '}Hard-hit {p.hard_hit_pct != null ? `${p.hard_hit_pct}%` : '—'} ·
          {' '}Launch {p.launch != null ? `${p.launch}°` : '—'} ·
          {' '}P9 expected {ops3(p.p9_expected)} OPS · OPS then {ops3(p.ops_then)}
        </div>
      )}
      {/* SAME-SITUATION CONDITION (2026-07-14): comps are direction-gated
          server-side (candidate's surface vs xwOBA anchor); the aggregate line
          IS the same-situation base rate — it corroborates the direction call.
          Thin pools (<5 same-direction matched cases) render honest thin copy,
          never a padded table. */}
      {profileComps.situation_thin ? (
        <div style={{ ...sub, marginTop: 6, fontStyle: 'italic' }}>
          Not enough same-situation history — comps are shown only for players in
          the same skill-vs-results situation, and this pool is too thin to be honest.
        </div>
      ) : profileComps.base_rate_line ? (
        <div style={{ color: C.neutral[200], fontSize: 12, marginTop: 6, fontWeight: 600 }}>
          {profileComps.base_rate_line}
        </div>
      ) : null}
      {comps.length > 0 && (
        <table style={{ width: '100%', marginTop: 6, fontSize: 12, color: C.neutral[200] }}>
          <thead>
            <tr style={sub}>
              <td>Player</td><td>Season</td><td>OPS then</td><td>P9 expected</td><td>What happened</td><td>Verdict</td>
            </tr>
          </thead>
          <tbody>
            {comps.map((c, i) => (
              <tr key={`${c.player_id}-${c.season}-${i}`}>
                <td>{c.name || `#${c.player_id}`}</td>
                <td>{c.season}</td>
                <td>{ops3(c.ops_then)}</td>
                <td>{ops3(c.p9_expected)}</td>
                <td>{ops3(c.what_happened)}</td>
                <td style={{ color: VERDICT_COLOR[c.verdict] || C.neutral[400] }}>{c.verdict || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {/* kNN conversion rate — DEMOTED to caveated secondary color (Phase 2): the
          12-comp rate is proven noise and is NOT the confidence driver (the header's
          divergence band is). */}
      {profileComps.comps_caveat && (
        <div style={{ ...sub, marginTop: 6, fontStyle: 'italic' }}>{profileComps.comps_caveat}</div>
      )}
      {/* FOOTNOTE — survivorship honesty (no longer the display concept).
          Skipped in the thin state: whole-pool stats under a withheld table
          would describe comps the panel refuses to show. */}
      {!profileComps.situation_thin && (
        <div style={{ ...sub, marginTop: 6, fontStyle: 'italic' }}>
          {profileComps.base_rate_pct != null && (
            <>Historically {profileComps.base_rate_pct}% of all matched profiles converted
            {profileComps.window_label ? ` (${profileComps.window_label})` : ''};
            {' '}{profileComps.vanish_pct}% vanished and count as did-not-convert (no survivorship inflation)
            {profileComps.median_gain_ops > 0 ? ` · median OPS gain among converters +${ops3(profileComps.median_gain_ops)}` : ''}.
            {' '}</>
          )}
          Comps without a rest-of-season sample are excluded from the verdict count.
        </div>
      )}
    </>
  );
};

const ProjectionInsights = ({ playerId, isPitcher, target }) => {
  // Bug #4: the trend target must match player type — pitchers default to ERA (a pitcher
  // stat), not the hitter 'HR' (home runs). Explicit caller-supplied targets still win.
  const t = target || (isPitcher ? 'ERA' : 'HR');
  const { il, anomaly, comparables, series, loading } = useProjectionInsights(playerId, { isPitcher, target: t });
  if (loading) return null;

  const hasTrend = !!(series && series.values && series.values.length > 0);
  const hasAnomaly = !!(anomaly && anomaly.gap != null);
  // IL Risk (Tony ruling 2026-07-15): render the section ONLY for a REAL
  // per-player payload — a rebuilt Cox model that clears its C-index gate
  // (top-level per-player horizons, not discrimination-gated). The
  // gated/population-baseline shape (same numbers for every player + an
  // explanation nobody asked for) is HIDDEN ENTIRELY: no card, gone.
  const hasIl = !!(il && il.horizons && il.discrimination_gated !== true
                   && il.presentation !== 'population_baseline');
  // Profile comps render for covered:true AND for the labeled absent states
  // (Gate 3: insufficient_sample / not_current); null = fetch failure.
  // DD-RO-BUGS6-0725 B3: Profile Comparables is a HITTER contact-profile feature. On a pitcher the
  // backend returns covered:false reason:'pitcher', but the card rendered unconditionally, so a pitcher
  // page showed a 'Profile Comparables' card whose only line was 'comps cover hitters' — hitter copy on
  // a pitcher. Suppress the whole card for pitchers (it never applies to them).
  const hasProfileComps = !isPitcher && !!(comparables && typeof comparables.covered === 'boolean');
  if (!hasIl && !hasAnomaly && !hasProfileComps && !hasTrend) return null;

  return (
    <div className="projection-insights">
      {hasTrend && (
        <CollapsibleCard title={`Projection Trend — ${t}`}>
          <ProjectionTrendBody series={series} target={t} />
        </CollapsibleCard>
      )}
      {hasIl && (
        <CollapsibleCard title="IL Risk">
          <ILRiskBody il={il} />
        </CollapsibleCard>
      )}
      {hasAnomaly && (
        <CollapsibleCard title="Anomaly">
          <AnomalyBody anomaly={anomaly} />
        </CollapsibleCard>
      )}
      {!isPitcher && (
        <CollapsibleCard title="Profile Comparables">
          {hasProfileComps ? (
            <ProfileCompsBody profileComps={comparables} />
          ) : (
            /* GRACEFUL DEGRADATION (2026-07-14): a stale/unreachable backend must say so, never
               render silent-empty against a new frontend. */
            <div style={{ fontSize: 12, opacity: 0.75 }}>
              Comparables are temporarily unavailable (service updating) — check back shortly.
            </div>
          )}
        </CollapsibleCard>
      )}
    </div>
  );
};

export default ProjectionInsights;
